<template>
    <div class="kv-phone-input" :class="{ 'is-open': dropdownOpen }">
        <div class="kv-phone-input__country" v-click-outside="closeDropdown" style="margin-top:10px">
            <button
                type="button"
                class="form-control kv-phone-input__country-toggle"
                :disabled="disabled"
                @click="toggleDropdown"
            >
                <span class="kv-phone-input__country-flag">{{ selectedFlag }}</span>
                <span class="kv-phone-input__country-dial">+{{ selectedDialCode }}</span>
                <span class="kv-phone-input__caret">▾</span>
            </button>
            <div v-if="dropdownOpen" class="kv-phone-input__dropdown">
                <input
                    ref="search"
                    type="text"
                    class="form-control kv-phone-input__search"
                    v-model="search"
                    :placeholder="searchPlaceholderText"
                    @keydown.esc="closeDropdown"
                />
                <ul class="kv-phone-input__list">
                    <li
                        v-for="country in filteredCountries"
                        :key="country.iso"
                        class="kv-phone-input__option"
                        :class="{ 'is-selected': country.iso === selectedIso }"
                        @click="selectCountry(country.iso)"
                    >
                        <span class="kv-phone-input__country-flag">{{ country.flag }}</span>
                        <span class="kv-phone-input__option-name">{{ country.name }}</span>
                        <span class="kv-phone-input__option-dial">+{{ country.dialCode }}</span>
                    </li>
                    <li
                        v-if="!filteredCountries.length"
                        class="kv-phone-input__empty"
                    >
                        {{ searchEmptyTextLabel }}
                    </li>
                </ul>
            </div>
        </div>
        <input
            ref="input"
            class="form-control kv-phone-input__number"
            type="tel"
            :placeholder="phonePlaceholder"
            :disabled="disabled"
            :value="internalValue"
            @input="onInput"
            @blur="$emit('blur', $event)"
        />
    </div>
</template>

<script>
import {
    countryOptions,
    processTypedNumber,
    parseFullNumber,
    exampleNationalFor,
} from "../../helpers/phone";

export default {
    name: "kv-phone-input",
    directives: {
        "click-outside": {
            bind(el, binding) {
                el.__kvClickOutside__ = (event) => {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.addEventListener("click", el.__kvClickOutside__);
            },
            unbind(el) {
                document.removeEventListener("click", el.__kvClickOutside__);
            },
        },
    },
    props: {
        value: { type: String, default: "" },
        placeholder: { type: String, default: "" },
        defaultCountry: { type: String, default: "BR" },
        disabled: { type: Boolean, default: false },
        searchPlaceholder: { type: String, default: "" },
        searchEmptyText: { type: String, default: "" },
    },
    data() {
        return {
            selectedIso: this.defaultCountry || "BR",
            internalValue: "",
            dropdownOpen: false,
            search: "",
        };
    },
    computed: {
        countries() {
            return countryOptions();
        },
        filteredCountries() {
            const term = this.search.trim().toLowerCase();
            if (!term) {
                return this.countries;
            }
            const digits = term.replace(/\D/g, "");
            return this.countries.filter((country) => {
                const name = country.name.toLowerCase();
                const iso = country.iso.toLowerCase();
                const dial = String(country.dialCode);
                return (
                    name.indexOf(term) !== -1 ||
                    iso.indexOf(term) !== -1 ||
                    (digits && dial.indexOf(digits) !== -1)
                );
            });
        },
        selectedCountry() {
            return (
                this.countries.find((c) => c.iso === this.selectedIso) || null
            );
        },
        selectedFlag() {
            return this.selectedCountry ? this.selectedCountry.flag : "";
        },
        selectedDialCode() {
            return this.selectedCountry ? this.selectedCountry.dialCode : "";
        },
        phonePlaceholder() {
            const example = exampleNationalFor(this.selectedIso);
            return example || this.placeholder;
        },
        searchPlaceholderText() {
            return this.searchPlaceholder || this.t("searchSelect-placeholder");
        },
        searchEmptyTextLabel() {
            return this.searchEmptyText || this.t("no_results_found");
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(newVal) {
                this.applyExternalValue(newVal);
            },
        },
        defaultCountry(newCountry) {
            if (!newCountry || newCountry === this.selectedIso) {
                return;
            }
            this.selectedIso = newCountry;
            this.applyExternalValue(this.value);
        },
    },
    methods: {
        focus() {
            if (this.$refs.input) {
                this.$refs.input.focus();
            }
        },
        toggleDropdown() {
            if (this.disabled) {
                return;
            }
            this.dropdownOpen = !this.dropdownOpen;
            if (this.dropdownOpen) {
                this.search = "";
                this.$nextTick(() => {
                    if (this.$refs.search) {
                        this.$refs.search.focus();
                    }
                });
            }
        },
        closeDropdown() {
            this.dropdownOpen = false;
        },
        selectCountry(iso) {
            this.selectedIso = iso;
            this.closeDropdown();
            this.emitForDigits(this.internalValue.replace(/\D/g, ""));
            this.$nextTick(() => this.focus());
        },
        applyExternalValue(newVal) {
            if (!newVal) {
                this.internalValue = "";
                return;
            }
            const raw = String(newVal).trim();
            // E.164 input: detect country and show only the national part.
            if (raw.indexOf("+") === 0) {
                const { national, isoCode } = parseFullNumber(
                    raw,
                    this.selectedIso
                );
                if (isoCode) {
                    this.selectedIso = isoCode;
                }
                this.internalValue = national;
                return;
            }
            const digits = raw.replace(/\D/g, "");
            const { national } = processTypedNumber(this.selectedIso, digits);
            this.internalValue = national;
        },
        emitForDigits(digits) {
            if (!digits) {
                this.internalValue = "";
                if (this.$refs.input) {
                    this.$refs.input.value = "";
                }
                this.$emit("input", "");
                this.$emit("parsed", {
                    isValid: false,
                    isoCode: this.selectedIso,
                    e164: "",
                });
                return;
            }
            const { national, e164, isValid, isoCode } = processTypedNumber(
                this.selectedIso,
                digits
            );
            this.internalValue = national;
            if (this.$refs.input) {
                this.$refs.input.value = national;
            }
            this.$emit("input", e164);
            this.$emit("parsed", { isValid, isoCode, e164 });
        },
        onInput(event) {
            const prevDigits = this.internalValue.replace(/\D/g, "");
            let digits = event.target.value.replace(/\D/g, "");
            const isDelete =
                event.target.value.length < this.internalValue.length ||
                (event.inputType &&
                    String(event.inputType).indexOf("delete") === 0);
            // When a separator (e.g. ")" or space) is deleted the digit count
            // is unchanged; the reformat would re-add it and trap the caret.
            // Remove the preceding digit instead so erasing keeps working.
            if (isDelete && digits === prevDigits && digits.length > 0) {
                digits = digits.slice(0, -1);
            }
            if (digits.length > 15) {
                digits = digits.slice(0, 15);
            }
            this.emitForDigits(digits);
        },
    },
};
</script>

<style scoped>
.kv-phone-input {
    display: flex;
    gap: 8px;
}
.kv-phone-input__country {
    position: relative;
    flex: 0 0 auto;
}
.kv-phone-input__country-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 96px;
    cursor: pointer;
    white-space: nowrap;
}
.kv-phone-input__country-toggle:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}
.kv-phone-input__country-flag {
    font-size: 1.1em;
}
.kv-phone-input__caret {
    margin-left: auto;
    font-size: 0.8em;
    opacity: 0.6;
}
.kv-phone-input__dropdown {
    position: absolute;
    z-index: 1050;
    top: calc(100% + 4px);
    left: 0;
    width: 280px;
    max-width: 80vw;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
    overflow: hidden;
}
.kv-phone-input__search {
    border: none;
    border-bottom: 1px solid #eee;
    border-radius: 0;
    width: 100%;
}
.kv-phone-input__list {
    list-style: none;
    margin: 0;
    padding: 4px 0;
    max-height: 240px;
    overflow-y: auto;
}
.kv-phone-input__option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    cursor: pointer;
}
.kv-phone-input__option:hover {
    background: #f5f5f5;
}
.kv-phone-input__option.is-selected {
    background: #eef6f0;
    font-weight: 600;
}
.kv-phone-input__option-name {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.kv-phone-input__option-dial {
    flex: 0 0 auto;
    color: #888;
}
.kv-phone-input__empty {
    padding: 12px;
    text-align: center;
    color: #999;
}
.kv-phone-input__number {
    flex: 1 1 auto;
}
</style>
